/**
 * Build POST body for POST /user/save-info-message
 */
export function normalizeMessageStatus(status) {
    const key = (status || "draft").toLowerCase();
    const map = {
        draft: "Draft",
        queued: "Queued",
        scheduled: "Scheduled",
        delivered: "Delivered",
        cancel: "Cancelled",
        cancelled: "Cancelled",
    };
    return map[key] || status || "Draft";
}

export function applyMessageRecordToForm(record) {
    const popup = record?.popup_data_obj ?? {};
    return {
        id: record?.id ?? null,
        title: popup.title ?? "",
        content: popup.msg ?? "",
        popupConfig: {
            type: popup.type ?? "info",
            action: popup.action ?? "",
        },
        status: normalizeMessageStatus(record?.status),
        userAccessKeyList: record?.user_access_key_list ?? [],
        targetPublishTime: record?.target_publish_time ?? 0,
    };
}

export function normalizePopupDataObj(popupDataObj) {
    return {
        title: popupDataObj?.title ?? "",
        msg: popupDataObj?.msg ?? "",
        type: popupDataObj?.type ?? "info",
        action: popupDataObj?.action ?? "",
    };
}

export function buildSaveInfoMessagePayload({
    id,
    userAccessKeyList = [],
    popupDataObj,
    targetPublishTime,
    status,
}) {
    const popup = normalizePopupDataObj(popupDataObj);
    const payload = {
        user_access_key_list: userAccessKeyList,
        popup_data_obj: popup,
        target_publish_time: targetPublishTime,
        status: status,
    };
    if (id != null && id !== "") {
        payload.id = id;
    }
    return payload;
}

export function nowUnixSec() {
    return Math.floor(Date.now() / 1000);
}

/** target_publish_time: Unix seconds; 0 / empty = unset. */
export function parseTargetPublishTimeUnixSec(value) {
    if (value == null || value === "" || value === 0) return null;

    let seconds = value;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed === "0") return null;
        if (!/^\d+$/.test(trimmed)) return null;
        seconds = Number(trimmed);
    }

    if (
        typeof seconds !== "number" ||
        !Number.isFinite(seconds) ||
        seconds <= 0
    ) {
        return null;
    }

    return seconds < 1e12 ? Math.floor(seconds) : Math.floor(seconds / 1000);
}

export function normalizeTargetPublishTime(value) {
    return parseTargetPublishTimeUnixSec(value) ?? 0;
}

export function parseTargetPublishTimeMs(value) {
    const sec = parseTargetPublishTimeUnixSec(value);
    return sec == null ? null : sec * 1000;
}

/** created_time: ISO datetime string from API. */
export function parseCreatedTimeUnixSec(value) {
    if (value == null || value === "" || typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const ms = Date.parse(trimmed);
    if (Number.isNaN(ms)) return null;
    const sec = Math.floor(ms / 1000);
    return sec > 0 ? sec : null;
}

export function parseCreatedTimeMs(value) {
    const sec = parseCreatedTimeUnixSec(value);
    return sec == null ? null : sec * 1000;
}

export function isPublishTimePast(value) {
    const targetSec = parseTargetPublishTimeUnixSec(value);
    return targetSec != null && targetSec < nowUnixSec();
}

/** Unix seconds for publish-now, +5 min, or scheduled date/time (local). */
export function resolveTargetPublishTime({
    publishOption,
    scheduleDate,
    scheduleTime,
}) {
    if (publishOption === "now") return nowUnixSec();
    if (publishOption === "5min") return nowUnixSec() + 5 * 60;
    if (publishOption === "later" && scheduleDate && scheduleTime) {
        const scheduled = new Date(`${scheduleDate}T${scheduleTime}`);
        if (!Number.isNaN(scheduled.getTime())) {
            return Math.floor(scheduled.getTime() / 1000);
        }
    }
    return nowUnixSec();
}
